import React from 'react';
import { Scale, Zap, ArrowDown, Lightbulb, Anchor } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * CompetingForcesCard
 *
 * Renders the AI-triggered "Competing Forces" analytical layer.
 * The card is only mounted by the parent when result.competing_forces?.triggered === true.
 * All fields (core_tension, force_one, force_two, where_tension_exists,
 * emerging_insight, relationship_to_verdict) are expected to be populated
 * (server-side sanitizer already demoted incomplete analyses to triggered=false).
 *
 * This card NEVER overrides the primary verdict — it sits between the
 * Real-time News Sources block and the Google Deep Dive section.
 */
const ForceTile = ({ force, side, testIdPrefix }) => {
  if (!force) return null;
  const sideStyle =
    side === 'one'
      ? 'from-indigo-50 to-slate-50 dark:from-indigo-950/40 dark:to-slate-800/40 border-indigo-200 dark:border-indigo-800/60'
      : 'from-amber-50 to-slate-50 dark:from-amber-950/40 dark:to-slate-800/40 border-amber-200 dark:border-amber-800/60';
  const labelStyle =
    side === 'one'
      ? 'text-indigo-700 dark:text-indigo-300'
      : 'text-amber-700 dark:text-amber-300';
  return (
    <div
      className={`flex-1 min-w-0 w-full p-3 sm:p-4 rounded-xl border bg-gradient-to-br ${sideStyle}`}
      data-testid={`${testIdPrefix}-tile`}
    >
      <div className="flex items-start gap-2 mb-2 min-w-0">
        <Zap className={`w-4 h-4 mt-0.5 flex-shrink-0 ${labelStyle}`} />
        <span
          className={`text-[11px] sm:text-sm font-semibold ${labelStyle} uppercase tracking-wide sm:tracking-wider leading-snug break-words min-w-0`}
          data-testid={`${testIdPrefix}-label`}
        >
          {force.label}
        </span>
      </div>
      <p
        className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 leading-relaxed break-words"
        data-testid={`${testIdPrefix}-description`}
      >
        {force.description}
      </p>
      {force.evidence && (
        <p
          className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-2 italic break-words"
          data-testid={`${testIdPrefix}-evidence`}
        >
          Evidence: {force.evidence}
        </p>
      )}
    </div>
  );
};

const CompetingForcesCard = ({ forces }) => {
  if (!forces || !forces.triggered) return null;

  return (
    <div
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 backdrop-blur-sm shadow-sm overflow-hidden"
      data-testid="competing-forces-card"
    >
      {/* Header band */}
      <div className="px-3 sm:px-5 py-2.5 sm:py-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/60 dark:to-slate-800/30 flex items-center gap-2 min-w-0">
        <Scale className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700 dark:text-slate-200 flex-shrink-0" />
        <h4
          className="text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-100 truncate min-w-0"
          data-testid="competing-forces-heading"
        >
          Competing Forces
        </h4>
        <Badge
          variant="secondary"
          className="ml-auto text-[9px] sm:text-[10px] uppercase tracking-wider bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex-shrink-0 px-1.5 py-0.5"
        >
          <span className="hidden sm:inline">AI-triggered analysis</span>
          <span className="sm:hidden">AI</span>
        </Badge>
      </div>

      <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
        {/* Core Tension */}
        {forces.core_tension && (
          <div>
            <p className="text-[11px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Core Tension
            </p>
            <p
              className="text-sm sm:text-base text-slate-800 dark:text-slate-100 leading-relaxed break-words"
              data-testid="competing-forces-core-tension"
            >
              {forces.core_tension}
            </p>
          </div>
        )}

        {/* Force One vs Force Two */}
        <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-2">
          <ForceTile force={forces.force_one} side="one" testIdPrefix="competing-forces-force-one" />
          <div className="flex sm:flex-col items-center justify-center text-slate-400 dark:text-slate-500 py-1 sm:px-1 sm:py-0" aria-hidden="true">
            <ArrowDown className="w-4 h-4 sm:hidden" />
            <span className="hidden sm:block text-[10px] uppercase tracking-widest font-semibold">
              vs
            </span>
          </div>
          <ForceTile force={forces.force_two} side="two" testIdPrefix="competing-forces-force-two" />
        </div>

        {/* Where Tension Exists */}
        {forces.where_tension_exists && (
          <div>
            <p className="text-[11px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Where the Tension Exists
            </p>
            <p
              className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 leading-relaxed break-words"
              data-testid="competing-forces-where"
            >
              {forces.where_tension_exists}
            </p>
          </div>
        )}

        {/* Emerging Insight */}
        {forces.emerging_insight && (
          <div className="p-2.5 sm:p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60">
            <div className="flex items-start gap-2 min-w-0">
              <Lightbulb className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] sm:text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider mb-1">
                  Emerging Insight
                </p>
                <p
                  className="text-xs sm:text-sm text-emerald-900 dark:text-emerald-100 leading-relaxed break-words"
                  data-testid="competing-forces-insight"
                >
                  {forces.emerging_insight}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Relationship to Verdict */}
        {forces.relationship_to_verdict && (
          <div className="p-2.5 sm:p-4 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
            <div className="flex items-start gap-2 min-w-0">
              <Anchor className="w-4 h-4 text-slate-600 dark:text-slate-300 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] sm:text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                  Relationship to Verdict
                </p>
                <p
                  className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 leading-relaxed break-words"
                  data-testid="competing-forces-verdict-relation"
                >
                  {forces.relationship_to_verdict}
                </p>
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-1 italic leading-snug">
                  This analysis does not change the primary verdict — it only surfaces the underlying tension.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetingForcesCard;
